import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, Upload, X, Image } from "lucide-react";

const FIELDS = [
  { k: "brand_name", l: "Brand name" },
  { k: "tagline", l: "Tagline" },
  { k: "hero_title", l: "Hero title" },
  { k: "hero_subtitle", l: "Hero subtitle", textarea: true },
  { k: "hero_image", l: "Hero background image URL" },
  { k: "footer_powered_by", l: "Footer powered-by text" },
  { k: "contact_email", l: "Contact email" },
  { k: "contact_phone", l: "Contact phone" },
  { k: "contact_address", l: "Contact address" },
];

const COLOR_FIELDS = [
  { k: "primary_color", l: "Primary", help: "Buttons, links and main brand fills" },
  { k: "secondary_color", l: "Secondary", help: "Accent gradients and highlight pills" },
  { k: "accent_color", l: "Accent (dark)", help: "Headings, titles and dark text" },
];

const PRODUCTS = [
  { k: "product_flight", l: "Flights" },
  { k: "product_hotel", l: "Hotels" },
  { k: "product_car", l: "Car rentals" },
  { k: "product_package", l: "Packages" },
  { k: "product_sightseeing", l: "Sightseeing" },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { reload: reloadSettings, setSettings: setLiveSettings } = useSettings();
  useEffect(() => { (async () => { const { data } = await api.get("/admin/settings"); setS(data); })(); }, []);
  if (!s) return <div className="text-slate-500">Loading...</div>;

  // Live preview: push edits into the global SettingsProvider so the entire
  // admin/site UI reflects color changes instantly (before saving to DB).
  const updateField = (k, v) => {
    const next = { ...s, [k]: v };
    setS(next);
    if (k === "primary_color" || k === "secondary_color" || k === "accent_color") {
      setLiveSettings((cur) => ({ ...cur, [k]: v }));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      FIELDS.forEach(f => patch[f.k] = s[f.k]);
      COLOR_FIELDS.forEach(f => patch[f.k] = s[f.k]);
      PRODUCTS.forEach(p => patch[p.k] = !!s[p.k]);
      await api.patch("/admin/settings", patch);
      await reloadSettings();
      toast.success("Saved — theme applied across the site");
    } catch (_e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be < 15 MB"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/settings/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, logo_url: data.logo_url });
      // Surface the optimisation result so admin sees the value-add.
      if (data.compression_ratio && data.compression_ratio > 1.2) {
        const before = (data.original_size_bytes / 1024).toFixed(0);
        const after  = (data.final_size_bytes / 1024).toFixed(0);
        toast.success(`Logo optimised — ${before} KB → ${after} KB (${data.compression_ratio}× smaller)`);
      } else {
        toast.success("Logo uploaded & optimised for the header");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearLogo = async () => {
    await api.patch("/admin/settings", { logo_url: "" });
    setS({ ...s, logo_url: "" });
    toast.success("Logo cleared — reverted to default");
  };

  const rawSetLogo = s.logo_url || "";
  const normSetLogo = rawSetLogo && !rawSetLogo.startsWith("http") && rawSetLogo.startsWith("/uploads/")
    ? `/api${rawSetLogo}`
    : rawSetLogo;
  const logoSrc = normSetLogo
    ? (normSetLogo.startsWith("http") ? normSetLogo : `${process.env.REACT_APP_BACKEND_URL}${normSetLogo}`)
    : "";

  return (
    <div>
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Site settings</h1>
      <p className="text-slate-500 mt-1">Brand, logo, colors, hero, contact info and product activation.</p>
      <Card className="mt-6 p-6 space-y-4 max-w-3xl">
        {/* Logo block */}
        <div className="border-b border-slate-100 pb-5">
          <Label className="block mb-2">Brand logo</Label>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {logoSrc
                ? <img src={logoSrc} alt="Logo preview" data-testid="logo-preview" className="max-w-full max-h-full object-contain p-2" />
                : <Image className="w-8 h-8 text-slate-300" />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onChange={onLogoChange}
                  disabled={uploading}
                  data-testid="logo-upload-input"
                  className="hidden"
                />
                <Button as="span" disabled={uploading} type="button" data-testid="logo-upload-btn"
                        className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"
                        onClick={(e) => e.currentTarget.previousSibling.click()}>
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : "Upload new logo"}
                </Button>
              </label>
              {s.logo_url && (
                <Button type="button" variant="outline" size="sm" onClick={clearLogo}
                        data-testid="logo-clear-btn" className="rounded-full">
                  <X className="w-3.5 h-3.5 mr-1" /> Use default
                </Button>
              )}
              <div className="text-xs text-slate-400 max-w-xs">PNG / JPG / SVG / WEBP — any size up to 15 MB. We&apos;ll auto-resize &amp; compress to fit the header.</div>
            </div>
          </div>
        </div>

        {FIELDS.map(f => (
          <div key={f.k}>
            <Label>{f.l}</Label>
            {f.textarea
              ? <Textarea data-testid={`set-${f.k}`} rows={2} value={s[f.k] || ""} onChange={(e) => updateField(f.k, e.target.value)} />
              : <Input data-testid={`set-${f.k}`} value={s[f.k] || ""} onChange={(e) => updateField(f.k, e.target.value)} />}
          </div>
        ))}

        {/* Theme color pickers — change is live across the site */}
        <div className="pt-3 border-t border-slate-100">
          <div className="font-medium text-[var(--brand-accent)] mb-3">Theme colors</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {COLOR_FIELDS.map(f => {
              const val = s[f.k] || "";
              return (
                <div key={f.k} className="border border-slate-200 rounded-xl p-3 bg-white">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{f.l}</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="color"
                      data-testid={`set-${f.k}-picker`}
                      value={val || "#000000"}
                      onChange={(e) => updateField(f.k, e.target.value)}
                      className="w-12 h-10 rounded-md border border-slate-200 cursor-pointer p-0 bg-white"
                      aria-label={`${f.l} color picker`}
                    />
                    <Input
                      data-testid={`set-${f.k}`}
                      value={val}
                      onChange={(e) => updateField(f.k, e.target.value)}
                      className="font-mono text-xs uppercase"
                      placeholder="#hexcolor"
                    />
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5">{f.help}</div>
                </div>
              );
            })}
          </div>
          {/* Live preview strip */}
          <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3" style={{ background: `linear-gradient(135deg, ${s.accent_color || "#134e4a"}, ${s.primary_color || "#14b8a6"})` }}>
              <div className="text-white text-sm font-heading font-semibold">Live preview</div>
              <div className="text-white/70 text-xs">Save to apply across the live B2C site</div>
            </div>
            <div className="px-4 py-3 flex items-center gap-2 bg-white">
              <button type="button" className="rounded-full px-4 h-9 text-sm text-white font-medium" style={{ background: s.primary_color || "#14b8a6" }}>Primary</button>
              <button type="button" className="rounded-full px-4 h-9 text-sm font-medium" style={{ background: s.secondary_color || "#86efac", color: s.accent_color || "#134e4a" }}>Secondary</button>
              <span className="text-base font-heading font-semibold ml-2" style={{ color: s.accent_color || "#134e4a" }}>Accent text</span>
            </div>
          </div>
        </div>
        <div className="pt-3 border-t border-slate-100">
          <div className="font-medium text-[var(--brand-accent)] mb-2">Activate / deactivate products on the website</div>
          {PRODUCTS.map(p => (
            <div key={p.k} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span>{p.l}</span>
              <Switch data-testid={`prod-${p.k}`} checked={!!s[p.k]} onCheckedChange={(v) => setS({ ...s, [p.k]: v })} />
            </div>
          ))}
        </div>
        <Button data-testid="save-settings" disabled={busy} onClick={save} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Saving..." : "Save settings"}
        </Button>
      </Card>
    </div>
  );
}
